import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";
import { Input } from "@/components/ui/input";
import { useSubjects } from "@/stores/appStore";
import { useRouter } from "next/navigation";
import { TextareaWithMaxLength } from "../fun-ui/TextareaWithMaxLength";
import { cn } from "@/lib/utils";

const SUBJECT_NAME_SIZE = 30;

// 统一的错误提示文案
export const ERROR_MESSAGE_NO_PAPERS_SELECTED = '请先选择需导入主题的文献';

type CreateOrEditSubjectDialogProps = React.PropsWithChildren<{
  subjectId?: string;
  subjectName?: string;
  importPaperIds?: string[];
  open?: boolean; // 受控模式：从外部控制 Dialog 的打开状态
  onOpenChange?: (open: boolean) => void;
  requireImportPapers?: boolean; // 是否需要验证文献（从 SubjectCreator 调用时需要）
  onSuccess?: () => void; // 成功创建并导入后的回调
}>

// 统一的成功 toast
const showSuccessToast = (subjectId: string, router: { push: (path: string) => void }) => {
  toast.success((t) => (
    <div className="flex items-center gap-3">
      <span>文献已导入主题</span>
      <button
        onClick={() => {
          router.push(`/subjects/${subjectId}`);
          toast.dismiss(t.id);
        }}
        className="text-primary hover:underline"
      >
        查看
      </button>
    </div>
  ), {
    duration: 4000,
  });
};

export function CreateOrEditSubjectDialog({
  subjectId = '',
  subjectName = '',
  children,
  importPaperIds = [],
  open: controlledOpen,
  onOpenChange,
  requireImportPapers = false,
  onSuccess,
}: CreateOrEditSubjectDialogProps) {
  const {
    processing,
    createSubject,
    renameSubject,
    importPaper,
  } = useSubjects();

  // 支持受控和非受控模式
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const [text, setText] = useState(subjectName);
  const router = useRouter();

  const reset = useCallback(() => {
    if (controlledOpen !== undefined) {
      // 受控模式：通过 onOpenChange 通知外部
      onOpenChange?.(false);
    } else {
      // 非受控模式：更新内部状态
      setInternalOpen(false);
      onOpenChange?.(false);
    }
  }, [controlledOpen, onOpenChange]);

  useEffect(() => {
    if (open) {
      setText(subjectName);
    }
  }, [open, subjectName]);

  const handleOpenChange = (newOpen: boolean) => {
    // 如果 requireImportPapers 为 true（从 SubjectCreator 调用），且创建新主题时没有选中文献，阻止打开对话框
    if (newOpen && requireImportPapers && !subjectId && (!importPaperIds || importPaperIds.length === 0)) {
      toast.error(ERROR_MESSAGE_NO_PAPERS_SELECTED);
      return;
    }
    if (controlledOpen !== undefined) {
      // 受控模式：通过 onOpenChange 通知外部
      onOpenChange?.(newOpen);
    } else {
      // 非受控模式：更新内部状态
      setInternalOpen(newOpen);
      onOpenChange?.(newOpen);
    }
  };

  const isCreatingWithImport = !subjectId && importPaperIds?.length > 0;
  const title = !subjectId ? "创建新的主题分类" : "修改主题分类";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="p-8 w-[480px]">
        <DialogDescription className="sr-only">{title}</DialogDescription>
        <DialogHeader>
          <DialogTitle className="text-lg leading-none">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {/* 显示导入文献提示 */}
          <div className="text-sm leading-none text-muted-foreground">
            {
              isCreatingWithImport ? `${importPaperIds.length} 篇文献将导入到` : "同一篇文献可以放入多个主题中"
            }
          </div>
          <div className="grid gap-2">
            <TextareaWithMaxLength
              placeholder={`示例：增强大型语言模型的代理能力（${SUBJECT_NAME_SIZE}字以内）`}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
              }}
              onFocus={(e) => {
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
              }}
              maxLength={SUBJECT_NAME_SIZE}
              rows={2}
              className={cn(
                "overflow-hidden border-none"
              )}
              containerClassName="py-0 border border-input bg-background"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => reset()}
            className="w-20"
          >
            取消
          </Button>
          <Button
            className={isCreatingWithImport ? "w-[120px] h-10" : "min-w-[120px]"}
            disabled={text.trim() === '' || text.trim() === subjectName || !!processing}
            onClick={async () => {
              if (subjectId) {
                // 重命名场景
                await renameSubject(subjectId, text.trim());
                reset();
              } else if (text.trim()) {
                // 创建新主题
                if (importPaperIds?.length) {
                  // 创建主题并导入文献：静默模式，统一显示一个 toast
                  const id = await createSubject(text.trim(), true);
                  if (id) {
                    await importPaper(id, importPaperIds.map(id => id.toString()), true);
                    // 显示成功 toast
                    showSuccessToast(id, router);
                    // 调用成功回调（用于重置选中和刷新列表）
                    onSuccess?.();
                  }
                } else {
                  // 仅创建主题（从 SubjectList 调用）：createSubject 会自动显示 toast
                  await createSubject(text.trim(), false);
                }
                // 不再自动跳转
                reset();
              } else {
                return;
              }
            }}
          >
            确认
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
